<template>
    <div class="max-w-6xl mx-auto bg-gray-50 p-6 sm:p-8 rounded-lg shadow-md">
        <h1 class="text-2xl font-bold text-gray-900 mb-6">{{ $translate('Payments & Invoices') }}</h1>

        <!-- Payment Cards -->
        <div v-if="payments.length > 0">
            <div v-for="payment in payments" :key="payment.id">
                <div :class="['mb-6 p-4 rounded-lg shadow-md', payment.status ? 'bg-green-50' : 'bg-red-50']">
                    <div class="flex justify-between items-start">
                        <div>
                            <h2 class="text-lg font-semibold text-gray-800">
                                {{ $translate('Order') }} #{{ payment.id }}
                            </h2>
                            <p class="text-sm text-gray-600">{{ $translate('Created at') }}: {{ payment.created_at }}
                            </p>
                            <p class="text-sm text-gray-600">
                                {{ $translate('Total Due') }}: {{ payment.due.formatted }}
                                <span v-if="payment.due.status && payment.due.price == 0" class="text-green-600">
                                    ({{ $translate('Order fully paid') }})
                                </span>
                                <span v-if="!payment.due.status && payment.due.price > 0" class="text-orange-600">
                                    ({{ $translate('Waiting payment') }})
                                </span>
                                <span v-if="payment.due.price < 0" class="text-orange-600">
                                    ({{ $translate('Pending Overpayment Resolution') }})
                                </span>
                            </p>
                        </div>

                        <!-- Status and Deferred Billing Badges -->
                        <div class="flex space-x-4 text-2xl">
                            <span v-if="payment.status" class="text-green-300 font-bold flex items-center">
                                <font-awesome-icon icon="check" class="mr-2" /> {{ $translate('Fulfilled') }}
                            </span>
                            <span v-else class="text-red-300 font-bold flex items-center">
                                <font-awesome-icon icon="close" class="mr-2" /> {{ $translate('Not Fulfilled') }}
                            </span>
                            <span v-if="payment.deferred_billing" class="text-orange-300 font-bold">
                                {{ $translate('Deferred Billing') }} ({{ $translate('Odložená fakturace') }})
                            </span>
                        </div>
                    </div>

                    <!-- Proforma Invoice Button -->
                    <div class="flex justify-end mt-2">
                        <a v-if="payment.items_count > 0" :href="payment.url"
                            class="text-blue-600 hover:text-blue-800 flex items-center" target="_blank">
                            <font-awesome-icon icon="file-download" class="mr-1" />
                            {{ $translate('View Proforma Invoice') }}
                        </a>
                        <i v-else class="text-orange-600">{{ $translate('No items to invoice') }}</i>
                    </div>

                    <!-- Invoices Table -->
                    <div v-if="payment.invoices && payment.invoices.length > 0" class="mt-4">
                        <h3 class="font-semibold text-gray-700">{{ $translate('Invoices') }}</h3>
                        <table class="w-full text-left table-auto mt-2 border">
                            <thead>
                                <tr class="bg-gray-200">
                                    <th class="p-2">{{ $translate('Invoice ID') }}</th>

                                    <th class="p-2">{{ $translate('Type') }}</th>
                                    <th class="p-2">{{ $translate('Total') }}</th>
                                    <th class="p-2">{{ $translate('Created At') }}</th>
                                    <th class="p-2 text-right"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="invoice in payment.invoices" :key="invoice.id" class="border-t">
                                    <td class="p-2">{{ invoice.id }}</td>
                                    <td class="p-2">{{ $translate(invoice.type === 'correction' ? 'Correction' :
                                        'Invoice')
                                        }}</td>
                                    <td class="p-2">{{ invoice.total }}</td>
                                    <td class="p-2">{{ invoice.created_at }}</td>
                                    <td class="p-2 text-right">
                                        <a :href="invoice.url"
                                            class="text-blue-600 hover:text-blue-800 flex items-center justify-end"
                                            target="_blank">
                                            <font-awesome-icon icon="file-download" class="mr-1" />
                                            {{ $translate('Download Invoice') }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Transactions Table -->
                    <div v-if="payment.transactions && payment.transactions.length > 0" class="mt-4">
                        <h3 class="font-semibold text-gray-700">{{ $translate('Payments') }}</h3>
                        <table class="w-full text-left table-auto mt-2 border">
                            <thead>
                                <tr class="bg-gray-200">
                                    <th class="p-2">{{ $translate('Transaction ID') }}</th>
                                    <th class="p-2">{{ $translate('Gateway') }}</th>
                                    <th class="p-2">{{ $translate('Total') }}</th>
                                    <th class="p-2">{{ $translate('Created At') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="transaction in payment.transactions" :key="transaction.id"
                                    :class="['border-t', transaction.refund ? 'text-red-500 line-through' : '']">
                                    <td class="p-2 flex items-center">
                                        <font-awesome-icon v-if="transaction.refund" icon="reply" class="mr-1" />
                                        {{ transaction.payment_reference }}
                                    </td>
                                    <td class="p-2">
                                        <img :src="transaction.gateway.logo" :alt="transaction.gateway.name"
                                            class="inline-block w-8 h-8 mr-2" />
                                        {{ transaction.gateway.name }}
                                    </td>
                                    <td class="p-2">{{ transaction.total }}</td>
                                    <td class="p-2">{{ transaction.created_at }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <!-- No Payments -->
        <div v-else class="text-center py-6">
            <p class="text-gray-600">{{ $translate('No payments found.') }}</p>
        </div>
    </div>
</template>

<script>
import { notify } from 'notiwind';
import { cookie } from '@/utils/cookie';

export default {

    data() {
        return {
            payments: [], // Will store the payments data
            fetched: false, // Add a flag to avoid multiple fetches
        };
    },
    mounted() {
        if (!this.fetched) {
            this.fetchPayments();
            this.fetched = true; // Mark as fetched after the first call
        }
    },
    beforeRouteUpdate(to, from, next) {
        // Only fetch again if necessary, or reset flag if conditions change
        if (!this.fetched) {
            this.fetchPayments();
        }
        next();
    },
    methods: {
        async fetchPayments() {
            try {
                const response = await fetch(`${window.vueAppConfig.endpoint}/api/v2/session/account/payments`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({ session_id: cookie(), brand_id: window.vueAppConfig.info.id }),
                });

                const data = await response.json();
                if (data.status && data.data.length > 0) {
                    this.payments = data.data;
                }/* else {
                    notify({
                        type: 'info',
                        group: 'info',
                        title: this.$translate('Error'),
                        text: data.message || this.$translate('No payments found.'),
                    });
                }*/
            } catch (error) {
                notify({
                    type: 'error',
                    group: 'error',
                    title: this.$translate('Error'),
                    text: error.message,
                });
            }
        },
    },
};
</script>
